// Order details › "Complaint" section — shown to staff when the booking is low-rated, so anyone
// opening the order from Appointments or CRM sees the case state and can jump to the queue.
// Read-only; all actions live in Rated Orders (RATED_ORDERS_PLAN.md §5.4). Providers see it too,
// with only the rating and the customer's comment — no case status, "Manage Case" or case details.
import moment from 'moment';
import React, { useState } from 'react';
import { Badge, Button, Label } from 'reactstrap';
import { Colxx } from '../../../../components/common/CustomBootstrap';
import Rating from '../../../../components/common/Rating';
import CaseModal from './CaseModal';
import { COMPLAINT_STATUS, PILL, RESOLUTION_TYPES, canManageComplaints, canViewRatedOrders } from './complaint';

const Item = ({ label, children }) => (
  <Colxx xxs="6" md="3" className="mb-2">
    <Label className="mb-0 text-semi-muted">{label}</Label>
    <p className="font-weight-bold m-0">{children || '—'}</p>
  </Colxx>
);

const ComplaintStrip = ({ booking, user, onChange }) => {
  const [caseOpen, setCaseOpen] = useState(false);
  if (!booking?.lowRated || !canViewRatedOrders(user)) return null;
  const canManage = canManageComplaints(user);
  const c = booking.complaint || {};
  const st = c.status || 'pending';
  const score = Number.isFinite(Number(booking.rate)) ? Number(booking.rate) : c.score;
  const comp = c.compensation;
  const total = Number(c.compensationTotal || comp?.amount || 0);
  const resolution = RESOLUTION_TYPES.find((r) => r.value === c.resolutionType)?.label || c.resolutionType;
  const accent = canManage && COMPLAINT_STATUS[st]?.color === 'success' ? '#3e884f' : '#c43d4b';
  return (
    <>
      <div className="border-bottom p-3 mb-5" />
      <h5 className="mt-4">Complaint</h5>
      <div style={{ borderRadius: '12px', border: '1px solid #eceff3', borderLeft: `4px solid ${accent}`, background: '#fbfbfc', padding: '14px 16px' }}>
        <div className="d-flex flex-wrap align-items-center" style={{ gap: '10px 14px' }}>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <Rating total={5} rating={Math.round(score || 0)} />
            <span className="font-weight-bold" style={{ color: '#c43d4b' }}>{score != null ? `${score} / 5` : 'Low rating'}</span>
          </div>
          {canManage && <Badge color={COMPLAINT_STATUS[st]?.color || 'secondary'} pill style={PILL} title={COMPLAINT_STATUS[st]?.hint}>{COMPLAINT_STATUS[st]?.label || st}</Badge>}
          <span className="text-muted text-small">{c.detectedAt ? `Flagged ${moment(c.detectedAt).format('DD/MM/YYYY hh:mm A')}` : ''}</span>
          {canManage && (
            <Button color="warning" size="sm" outline className="ml-auto d-inline-flex align-items-center" onClick={() => setCaseOpen(true)}>
              Manage Case
            </Button>
          )}
        </div>
        {booking.reason ? (
          <div className="mt-3" style={{ padding: '10px 12px', borderRadius: '8px', background: '#fff', border: '1px solid #eceff3', fontStyle: 'italic', color: '#4a4a4a' }}>
            “{booking.reason}”
          </div>
        ) : null}
        {canManage && <div className="row mt-3">
          <Item label="Assignee">{c.assigneeName}</Item>
          <Item label="Compensation">{total > 0 ? <span style={{ color: '#3e884f' }}>AED {total.toFixed(2)}{comp?.issuedAt ? <span className="text-muted font-weight-normal"> · {moment(comp.issuedAt).format('DD/MM/YYYY')}</span> : null}</span> : null}</Item>
          <Item label="Resolved">{st === 'resolved' && c.resolvedAt ? `${moment(c.resolvedAt).format('DD/MM/YYYY')}${c.resolvedBy ? ` · ${c.resolvedBy}` : ''}` : null}</Item>
          <Item label="Resolution">{st === 'resolved' ? resolution : null}</Item>
        </div>}
        {canManage && st === 'resolved' && c.resolutionNote ? <div className="text-muted text-small">Note: {c.resolutionNote}</div> : null}
      </div>
      {canManage && <CaseModal booking={booking} user={user} isOpen={caseOpen} dismiss={() => setCaseOpen(false)}
        onUpdated={(patch) => onChange && onChange({ ...patch, complaint: { ...(booking.complaint || {}), ...(patch.complaint || {}) } })} />}
    </>
  );
};

export default ComplaintStrip;
